import { createSlice, type PayloadAction as IPayloadAction } from "@reduxjs/toolkit";
import type { IRoutingGraph } from "@multiconsult-group/multiplacer-core";
import type { IRootState } from "../app/store";
import type { IProjectViewerColorTemplateName } from "./projectViewerColorTemplates";

export type IProjectViewerState = {
  selectedFileFloorKeys: string[];
  colorTemplateName: IProjectViewerColorTemplateName;
  measurementToolActive: boolean;
  earthingCalculationResult: IRoutingGraph | null;
  selectedEarthingApiRequestId: string | null;
  selectedEarthingApiRequestLiveResult: IRoutingGraph | null;
  selectedEarthingApiRequestLiveError: string | null;
  zClipRangeM: {
    bottomM: number | null;
    topM: number | null;
  };
};

const initialState: IProjectViewerState = {
  selectedFileFloorKeys: [],
  colorTemplateName: "jording",
  measurementToolActive: false,
  earthingCalculationResult: null,
  selectedEarthingApiRequestId: null,
  selectedEarthingApiRequestLiveResult: null,
  selectedEarthingApiRequestLiveError: null,
  zClipRangeM: {
    bottomM: null,
    topM: null,
  },
};

export const projectViewerSlice = createSlice({
  name: "projectViewer",
  initialState,
  reducers: {
    setSelectedFileFloorKeys: (state, action: IPayloadAction<string[]>) => {
      state.selectedFileFloorKeys = Array.from(new Set(action.payload));
    },
    toggleSelectedFileFloorKey: (state, action: IPayloadAction<string>) => {
      const fileFloorKey = action.payload;
      if (state.selectedFileFloorKeys.includes(fileFloorKey)) {
        state.selectedFileFloorKeys = state.selectedFileFloorKeys.filter(
          (key) => key !== fileFloorKey
        );
        return;
      }

      state.selectedFileFloorKeys.push(fileFloorKey);
    },
    clearSelectedFileFloorKeys: (state) => {
      state.selectedFileFloorKeys = [];
    },
    setProjectViewerColorTemplateName: (
      state,
      action: IPayloadAction<IProjectViewerColorTemplateName>
    ) => {
      state.colorTemplateName = action.payload;
    },
    setProjectViewerMeasurementToolActive: (state, action: IPayloadAction<boolean>) => {
      state.measurementToolActive = action.payload;
    },
    setProjectViewerEarthingCalculationResult: (
      state,
      action: IPayloadAction<IRoutingGraph | null>
    ) => {
      state.earthingCalculationResult = action.payload;
    },
    setProjectViewerSelectedEarthingApiRequestId: (
      state,
      action: IPayloadAction<string | null>
    ) => {
      state.selectedEarthingApiRequestId = action.payload;
      state.selectedEarthingApiRequestLiveResult = null;
      state.selectedEarthingApiRequestLiveError = null;
    },
    setProjectViewerSelectedEarthingApiRequestLiveResult: (
      state,
      action: IPayloadAction<IRoutingGraph | null>
    ) => {
      state.selectedEarthingApiRequestLiveResult = action.payload;
      state.selectedEarthingApiRequestLiveError = null;
    },
    setProjectViewerSelectedEarthingApiRequestLiveError: (
      state,
      action: IPayloadAction<string | null>
    ) => {
      state.selectedEarthingApiRequestLiveError = action.payload;
      state.selectedEarthingApiRequestLiveResult = null;
    },
    setProjectViewerZClipRangeM: (
      state,
      action: IPayloadAction<IProjectViewerState["zClipRangeM"]>
    ) => {
      state.zClipRangeM = action.payload;
    },
    updateProjectViewerZClipRangeM: (
      state,
      action: IPayloadAction<Partial<IProjectViewerState["zClipRangeM"]>>
    ) => {
      state.zClipRangeM = {
        ...state.zClipRangeM,
        ...action.payload,
      };
    },
  },
});

export const {
  setSelectedFileFloorKeys,
  toggleSelectedFileFloorKey,
  clearSelectedFileFloorKeys,
  setProjectViewerColorTemplateName,
  setProjectViewerEarthingCalculationResult,
  setProjectViewerMeasurementToolActive,
  setProjectViewerSelectedEarthingApiRequestId,
  setProjectViewerSelectedEarthingApiRequestLiveError,
  setProjectViewerSelectedEarthingApiRequestLiveResult,
  setProjectViewerZClipRangeM,
  updateProjectViewerZClipRangeM,
} = projectViewerSlice.actions;

export const selectProjectViewerSelectedFileFloorKeys = (state: IRootState) =>
  state.projectViewer.selectedFileFloorKeys;

export const selectProjectViewerColorTemplateName = (state: IRootState) =>
  state.projectViewer.colorTemplateName;

export const selectProjectViewerMeasurementToolActive = (state: IRootState) =>
  state.projectViewer.measurementToolActive;

export const selectProjectViewerEarthingCalculationResult = (state: IRootState) =>
  state.projectViewer.earthingCalculationResult;

export const selectProjectViewerSelectedEarthingApiRequestId = (state: IRootState) =>
  state.projectViewer.selectedEarthingApiRequestId;

export const selectProjectViewerSelectedEarthingApiRequestLiveResult = (state: IRootState) =>
  state.projectViewer.selectedEarthingApiRequestLiveResult;

export const selectProjectViewerSelectedEarthingApiRequestLiveError = (state: IRootState) =>
  state.projectViewer.selectedEarthingApiRequestLiveError;

export const selectProjectViewerZClipRangeM = (state: IRootState) =>
  state.projectViewer.zClipRangeM;
